import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Avatar from "@mui/material/Avatar";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Rating from "@mui/material/Rating";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
// adjust import path if getMyReviews is exported from a different module
import { getMyReviews, updateReview, deleteReview } from "../services/UserApi/UserApi";
import { resolveImageUrl, MEDIA_BASE_URL } from "../services/indexMediaBaseUrl";
import { useTranslation } from "react-i18next";

export default function MyReviews() {
  const { t, i18n } = useTranslation("MyReviews");
  // determine display language (prefer i18n then localStorage)
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const getName = (p) => (p ? (lang === "ar" ? (p.name_ar || p.name_en) : (p.name_en || p.name_ar)) : "");

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // new: editing state per review id and saving flags
  const [editing, setEditing] = useState({});
  const [tempValues, setTempValues] = useState({}); // { [id]: { rating, review } }
  const [saving, setSaving] = useState({});
  // new: deleting state per review id
  const [deleting, setDeleting] = useState({});

  useEffect(() => {
    let mounted = true;
     const fetch = async () => {
       setLoading(true);
       setError("");
       try {
         const res = await getMyReviews();
         // normalize response: res?.reviews or res?.data?.reviews or res?.data
         const data = res?.data?.reviews || res?.reviews || res?.data || [];
         if (mounted) setReviews(Array.isArray(data) ? data : []);
       } catch (err) {
         if (mounted) setError(err?.response?.data?.message || err?.message || t("fetch_reviews_failed"));
         // eslint-disable-next-line no-console
         console.error("MyReviews:getMyReviews error", err);
       } finally {
         if (mounted) setLoading(false);
       }
     };
     fetch();
     return () => { mounted = false; };
   }, []);

  const buildImageSrc = (photo) => {
    if (!photo) return "/placeholder.png";
    if (/^https?:\/\//i.test(photo)) return photo;
    const base = (MEDIA_BASE_URL || "").replace(/\/$/, "");
    const path = String(photo).replace(/^\/+/, "");
    if (base) return `${base}/${path}`;
    return resolveImageUrl(photo);
  };

  const startEdit = (r) => {
    const id = r.id;
    setEditing(prev => ({ ...prev, [id]: true }));
    setTempValues(prev => ({ ...prev, [id]: { rating: Number(r.rating) || 0, review: r.review || "" } }));
  };

  const cancelEdit = (id) => {
    setEditing(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
    setTempValues(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const handleTempChange = (id, field, value) => {
    setTempValues(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const saveEdit = async (id) => {
    const vals = tempValues[id];
    if (!vals) return;
    try {
      setSaving(prev => ({ ...prev, [id]: true }));
      // call API updateReview(reviewId, rating, reviewText)
      const res = await updateReview(id, { rating: vals.rating, review: vals.review });
      // update local reviews list
      setReviews(prev => prev.map(r => (r.id === id ? { ...r, rating: vals.rating, review: vals.review } : r)));
      window.alert(res?.message || t("review_updated"));
      cancelEdit(id);
    } catch (err) {
      console.error("updateReview error:", err);
      window.alert(err?.response?.data?.message || err?.message || t("review_update_failed"));
    } finally {
      setSaving(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      setDeleting(prev => ({ ...prev, [id]: true }));
      const res = await deleteReview(id);
      setReviews(prev => prev.filter(r => r.id !== id));
      window.alert(res?.message || t("review_deleted"));
    } catch (err) {
      console.error("deleteReview error:", err);
      window.alert(err?.response?.data?.message || err?.message || t("review_delete_failed"));
    } finally {
      setDeleting(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  if (loading) return <Box sx={{ p: 2, textAlign: "center" }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>;

  if (!reviews.length) return <Box sx={{ p: 2 }}><Typography>{t("no_reviews")}</Typography></Box>;

  return (
    <Box sx={{ p: 1 }}>
      <List>
        {reviews.map((rev) => {
          const r = rev || {};
          const prod = r.product || {};
          const photo = prod.main_photo || (prod.images && prod.images[0] && prod.images[0].image) || "";
          const img = buildImageSrc(photo);
          const isEditing = !!editing[r.id];
          const vals = tempValues[r.id] || {};
          const isSaving = !!saving[r.id];
          const isDeleting = !!deleting[r.id];

          return (
            <React.Fragment key={r.id || `${r.product_id}_${r.id}`}>
              <ListItem alignItems="flex-start" sx={{ py: 1 }}>
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={img}
                    alt={getName(prod) || String(prod.id || r.product_id)}
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/placeholder.png"; }}
                    sx={{ width: 64, height: 56, mr: 1 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="subtitle2">{getName(prod) || `${t("product")} ${r.product_id}`}</Typography>
                        {!isEditing ? (
                          <Rating value={Number(r.rating) || 0} readOnly size="small" />
                        ) : (
                          <Rating
                            value={Number(vals.rating) || 0}
                            onChange={(_, v) => handleTempChange(r.id, "rating", v)}
                            size="small"
                          />
                        )}
                      </Box>
                      {!isEditing ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" onClick={() => startEdit(r)}>{t("edit")}</Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleDelete(r.id)}
                            disabled={!!deleting[r.id]}
                          >
                            {deleting[r.id] ? t("deleting") : t("delete")}
                          </Button>
                        </Box>
                      ) : null}
                    </Box>
                  }
                  secondary={
                    <>
                      {!isEditing ? (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {r.review || ""}
                        </Typography>
                      ) : (
                        <TextField
                          value={vals.review ?? ""}
                          onChange={(e) => handleTempChange(r.id, "review", e.target.value)}
                          multiline
                          minRows={2}
                          fullWidth
                          variant="outlined"
                          size="small"
                        />
                      )}
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                      </Typography>
                      {isEditing && (
                        <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                          <Button size="small" variant="contained" onClick={() => saveEdit(r.id)} disabled={isSaving}>
                            {isSaving ? t("saving") : t("save")}
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => cancelEdit(r.id)} disabled={isSaving}>
                            {t("cancel")}
                          </Button>
                        </Box>
                      )}
                    </>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
}
